import { motion } from 'framer-motion';
import { useRedux } from 'hooks/useRedux';
import { useRootModal } from 'hooks/useRootModal';
import { useTranslate } from 'hooks/useTranslate';
import { ReflectContext } from 'pages/Growth/Reflect/Reflect';
import React, { useContext, useEffect, useState } from 'react';
import { addSparkToReflection } from 'utilities/addSparkToReflection';
import {
  FilteredActivities,
  filterActivities,
} from 'utilities/getFilterActivities';
import { ReflectionItemContext } from 'pages/Growth/Reflect/components/ReflectionItem/ReflectionItem';
import { getInitialReflectionSaveState } from 'utilities/getInitialReflectionSaveState';
import Button from '../Button/Button';
import Modal from '../Modal/Modal';
import './SparkLinkModal.module.scss';

type SparkLinkModalProps = {
  setSparkLinkModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  allSelectedActivities: FilteredActivities[];
  setAllSelectedActivities: React.Dispatch<
    React.SetStateAction<FilteredActivities[]>
  >;
  sparkName?: SparkEntity;
};

export default function SparkLinkModal({
  setSparkLinkModalIsOpen,
  setAllSelectedActivities,
  allSelectedActivities,
  sparkName,
}: SparkLinkModalProps) {
  const activities = useRedux((state) =>
    state.activity?.entities.allIds.map(
      (id) => state.activity?.entities.byId[id]
    )
  );

  const trainings = useRedux((state) =>
    state.training?.entities.allIds.map(
      (id) => state.training?.entities.byId[id]
    )
  );

  const { handleSaveReflection, project, endPointActivities } = useContext(
    ReflectContext
  );

  const filteredReflections = useRedux((state) =>
    state.reflection?.entities.allIds
      .map((id) => state.reflection?.entities.byId[id])
      .filter(
        (reflection) =>
          (reflection?.project_process?.project_id === project?.id &&
            reflection?.project_process?.process_type === 'App\\Activity' &&
            reflection?.created_by === state?.authentication?.user.id) ||
          (reflection?.project_process?.project_id === project?.id &&
            reflection?.project_process?.process_type === 'App\\Training' &&
            reflection?.created_by === state?.authentication?.user.id)
      )
  );

  const { save } = useContext(ReflectionItemContext);

  // useEffect that fires when modal mounts so we can create reflectionSaveStates for
  // the activities that don't have a reflection yet
  useEffect(() => {
    // Get the ids from filteredReflections to check against
    const reflectionIds = filteredReflections?.map(
      (reflection) => reflection?.project_process_id
    );

    // Filter out the activities that don't have a matching project_process_id in filteredReflections
    const unmatchedActivities = endPointActivities.filter((activity) => {
      const process_id = activity?.id as number;
      return !reflectionIds?.includes(process_id);
    });

    // Update the saveState with the unmatched project_process_ids
    const unmatchedIds = unmatchedActivities.map((activity) => {
      const project_process_id = activity?.id as number;

      const newSaveState = getInitialReflectionSaveState(project_process_id);
      // Save the updated state whenever saveState changes
      save(newSaveState);

      // Return the project_process_id so we can use it in the unmatchedIds array
      return project_process_id;
    });

    console.log('unmatchedIds', unmatchedIds);
  }, []);

  const filteredActivities = filterActivities(
    activities,
    endPointActivities,
    trainings
  );

  const translate = useTranslate();

  const [selectedActivities, setSelectedActivities] = useState<
    ActivityEntity[]
  >([]);
  const [selectedTrainings, setSelectedTrainings] = useState<TrainingEntity[]>(
    []
  );

  type EntityType = 'activity' | 'training';

  const addActivities = (
    item: ActivityEntity | TrainingEntity,
    entityType: EntityType
  ) => {
    if (entityType === 'activity') {
      const activity = item as ActivityEntity;

      setSelectedActivities((prevActivities) => {
        const existingActivity = prevActivities.find(
          (p) => p.id === activity.id
        );
        if (existingActivity) {
          return prevActivities.filter((p) => p.id !== activity.id);
        }
        return [...prevActivities, activity];
      });
    } else if (entityType === 'training') {
      const training = item as TrainingEntity;

      setSelectedTrainings((prevTrainings) => {
        const existingTraining = prevTrainings.find(
          (p) => p.id === training.id
        );
        if (existingTraining) {
          return prevTrainings.filter((p) => p.id !== training.id);
        }
        return [...prevTrainings, training];
      });
    }

    // Update allSelectedActivities with selectedActivities and selectedTrainings
    setAllSelectedActivities([
      { activities: selectedActivities, trainings: selectedTrainings },
    ]);
  };

  // Update allSelectedActivities when selectedActivities or selectedTrainings change
  useEffect(() => {
    setAllSelectedActivities([
      { activities: selectedActivities, trainings: selectedTrainings },
    ]);
  }, [selectedActivities, selectedTrainings]);

  const { closeModal } = useRootModal();

  const handleClose = () =>
    setSparkLinkModalIsOpen ? setSparkLinkModalIsOpen(false) : closeModal();

  return (
    <Modal setModalIsOpen={setSparkLinkModalIsOpen} size="medium" noCross>
      {/* TODO: translate */}
      <h2 styleName="header">Waar in het proces heb je deze spark behaald?</h2>
      <p styleName="spark">{sparkName?.name}</p>
      {/* List of activities */}
      <div styleName="container">
        {filteredActivities.map((filteredActivity, i) => (
          <React.Fragment key={i}>
            {filteredActivity?.activities?.map((activity) => (
              <motion.div
                onClick={() => addActivities(activity!, 'activity')}
                styleName={`wrapper ${
                  selectedActivities.find((p) => p.id === activity?.id)
                    ? 'selected'
                    : ''
                }`}
                key={`activity_${activity?.id}`}
              >
                <img
                  styleName="image"
                  alt={activity?.slug}
                  src={activity?.thumbnail}
                />
                <p styleName="name">{activity?.name}</p>
              </motion.div>
            ))}

            {filteredActivity?.trainings?.map((training) => (
              <motion.div
                onClick={() => addActivities(training!, 'training')}
                styleName={`wrapper ${
                  selectedTrainings.find((p) => p.id === training?.id)
                    ? 'selected'
                    : ''
                }`}
                key={`training_${training?.id}`}
              >
                <img
                  styleName="image"
                  alt={training?.title}
                  src="/images/training-icon.svg"
                />
                <p styleName="name">{training?.title}</p>
              </motion.div>
            ))}
          </React.Fragment>
        ))}
      </div>
      <div styleName="button-wrapper ">
        <Button
          color="grey"
          onClick={() => {
            setAllSelectedActivities([]);
            handleClose();
          }}
        >
          {translate('general.terms.cancel')}
        </Button>
        <Button
          onClick={() => {
            handleClose();
            const updatedReflections = addSparkToReflection(
              allSelectedActivities,
              filteredReflections,
              sparkName!
            );
            handleSaveReflection(updatedReflections);
            setAllSelectedActivities([]);
          }}
        >
          {translate('general.terms.confirm')}
        </Button>
      </div>
    </Modal>
  );
}
